import React, { useState } from 'react';
import { getSource } from '../utils/requestAccessSource';
import { trackEvent } from '../utils/clarity';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '/api';
const ROLE_OPTIONS = ['Project Manager', 'Superintendent', 'Scheduler', 'Executive', 'Other'];

interface Props {
  open: boolean;
  onClose: () => void;
}

export const RequestAccessModal: React.FC<Props> = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [schedulingSoftware, setSchedulingSoftware] = useState('');
  const [projectName, setProjectName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const reset = () => {
    setName(''); setEmail(''); setRole(''); setCompany(''); setSchedulingSoftware(''); setProjectName(''); setMessage('');
    setError(null); setSuccess(false); setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !role.trim() || !company.trim()) {
      setError('Please fill in name, email, role, and company.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          role: role.trim(),
          company: company.trim(),
          scheduling_software: schedulingSoftware.trim(),
          project_name: projectName.trim(),
          message: message.trim(),
          source: getSource(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as { error?: string }));
        throw new Error(body.error || 'Something went wrong. Please try again.');
      }
      setSuccess(true);
      trackEvent('request_access_submitted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-[12px] shadow-xl max-w-md w-full p-6 max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {success ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-tint flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-text mb-1">Thanks — we'll be in touch shortly</h3>
            <p className="text-sm text-muted mb-4">We've received your request and will reach out soon.</p>
            <button
              onClick={handleClose}
              className="text-sm font-semibold text-white bg-accent px-5 py-2.5 rounded-[8px] hover:bg-link transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-text mb-1">Request Access</h3>
            <p className="text-sm text-muted mb-4">Tell us a bit about your team and we'll follow up.</p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <TextField label="Name" required value={name} onChange={setName} />
              <TextField label="Email" type="email" required value={email} onChange={setEmail} />
              <SelectField label="Role" required value={role} onChange={setRole} options={ROLE_OPTIONS} />
              <TextField label="Company" required value={company} onChange={setCompany} />
              <TextField label="What scheduling software do you use?" value={schedulingSoftware} onChange={setSchedulingSoftware} />
              <TextField label="Project name (if you have one)" value={projectName} onChange={setProjectName} />
              <TextareaField label="Why are you interested?" value={message} onChange={setMessage} />

              {error && (
                <p className="text-xs text-crit-text bg-crit-bg px-3 py-2 rounded-[8px]">{error}</p>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 text-sm font-medium text-muted bg-gray-50 py-2.5 rounded-[8px] hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 text-sm font-semibold text-white bg-accent py-2.5 rounded-[8px] disabled:opacity-50 hover:bg-link transition-colors"
                >
                  {submitting ? 'Sending…' : 'Submit'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const TextField: React.FC<{ label: string; value: string; onChange: (v: string) => void; required?: boolean; type?: string }> = ({
  label, value, onChange, required, type = 'text',
}) => (
  <div>
    <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1">
      {label}{required && ' *'}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full text-sm border border-gray-200 rounded-[8px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
    />
  </div>
);

const SelectField: React.FC<{ label: string; value: string; onChange: (v: string) => void; options: string[]; required?: boolean }> = ({
  label, value, onChange, options, required,
}) => (
  <div>
    <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1">
      {label}{required && ' *'}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full text-sm border border-gray-200 rounded-[8px] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-accent"
    >
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const TextareaField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      className="w-full text-sm border border-gray-200 rounded-[8px] px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-accent"
    />
  </div>
);
